/**
 * Catálogo único de seções do app.
 *
 * Campos legados (mantidos para compatibilidade com telas antigas):
 *   `tab`, `module`, `action`, `toggle`, `pro`, `icon`, `keyword`, `description`, `categoryId`.
 *
 * Campos da Home B2C:
 *   `placements` — onde a seção aparece: 'content' (painel Todas as seções) e/ou 'tools' (painel Ferramentas).
 *   `homeOrder`  — posição nos atalhos de "Acesso rápido" no desktop.
 *   `homeMobileOrder` — posição no "Acesso rápido" do mobile: o 1 vira o banner
 *                  de três colunas, o 2 fica ao lado dele e os seguintes formam
 *                  a segunda linha.
 *   `toolsOrder` — posição no painel Ferramentas.
 *   `shortName`  — rótulo curto (sentence case) usado nos cards da Home e do painel Ferramentas.
 *   `subtitle`   — descrição curta exibida nos cards do painel Ferramentas.
 *   `contentTopics` — valores de `item.topic` do `global_summary` que apontam
 *                  para esta seção, quando diferem de `id` e `name`.
 *
 * `pro` é a única fonte de verdade para a exibição do selo PRO.
 */
const sections = [
    {
        name: "Flashcards",
        module: "preferences",
        action: "openCardsMenu",
        id: 'cards',
        icon: 'flashcardsIcon',
        keyword: 'cards',
        tab: 0,
        pro: true,
        placements: ['content', 'tools'],
        homeOrder: 7,
        toolsOrder: 1,
        subtitle: "Estudo e revisão",
        description: "Reforce e avalie seus conhecimentos com cartões interativos.",
    },
    {
        name: "Medicamentos e Vacinas",
        shortName: "Medicamentos e vacinas",
        icon: 'drugs',
        module: "preferences",
        action: "openDrugs",
        tab: 0,
        placements: ['content'],
        homeOrder: 1,
        homeMobileOrder: 1,
        highlighted: true,
        description: "Informações detalhadas sobre medicamentos, vacinas e doses."
    },
    {
        name: "Tratamento",
        module: "topics",
        action: "homeTopicClick",
        id: 'treatment',
        icon: 'treatment',
        keyword: 'tratamento',
        tab: 0,
        pro: true,
        placements: ['content'],
        homeOrder: 2,
        homeMobileOrder: 2,
        description: "Encontre opções de tratamento eficazes para diversas condições clínicas."
    },
    {
        name: "Doenças",
        module: "topics",
        action: "homeTopicClick",
        id: 'diseases',
        icon: 'diseases',
        keyword: 'doença',
        tab: 0,
        pro: true,
        placements: ['content'],
        homeOrder: 3,
        homeMobileOrder: 4,
        description: "Saiba informações detalhadas sobre doenças em cães e gatos."
    },
    {
        name: "Folhetos do Tutor",
        shortName: "Folhetos do tutor",
        module: "topics",
        action: "clinicalHandoutsHomeClick",
        id: 'handouts',
        icon: 'handouts',
        keyword: 'folhetos',
        toggle: 'handouts',
        tab: 1,
        pro: false,
        placements: ['content', 'tools'],
        homeMobileOrder: 5,
        toolsOrder: 6,
        subtitle: "Orientações para compartilhar",
        description: "Informe o tutor e aumente a eficácia do tratamento de seus pacientes."
    },
    {
        name: "Algoritmos Interativos",
        shortName: "Algoritmos interativos",
        module: "topics",
        action: "homeTopicClick",
        id: 'algorithms',
        icon: 'algorithms',
        keyword: 'algoritmo',
        // `global_summary` traz esses itens com topic "Algoritmos".
        contentTopics: ['Algoritmos'],
        tab: 1, pro: true,
        placements: ['content', 'tools'],
        homeMobileOrder: 6,
        toolsOrder: 3,
        subtitle: "Condutas guiadas",
        description: "Algoritmos interativos para auxiliar na tomada de decisões clínicas."
    },
    {
        name: "Calculadoras e Escores",
        shortName: "Calculadoras e escores",
        module: "calculators",
        action: "openCalculators",
        icon: 'calculators',
        categoryId: 7,
        keyword: 'calculadora',
        tab: 1,
        placements: ['content', 'tools'],
        homeMobileOrder: 3,
        toolsOrder: 2,
        subtitle: "Apoio à rotina clínica",
        description: "Utilize calculadoras e escores para diagnósticos e tratamentos precisos."
    },
    {
        name: "Diagnósticos Diferenciais",
        shortName: "Diagnósticos diferenciais",
        module: "ddx",
        action: "openDdx",
        icon: 'ddx',
        tab: 1, pro: true,
        placements: ['content', 'tools'],
        toolsOrder: 4,
        subtitle: "Consulta por sinais",
        description: "Diagnósticos diferenciais completos a partir de sinais clínicos."
    },
    {
        name: "Interações Medicamentosas",
        shortName: "Interações medicamentosas",
        module: "drugs",
        action: "openDrugsInteractions",
        icon: 'drugsInteractions',
        tab: 1, pro: true,
        placements: ['content', 'tools'],
        toolsOrder: 5,
        subtitle: "Checagem rápida",
        description: "Verifique interações medicamentosas para garantir tratamentos seguros."
    },
    {
        name: "Suplementos",
        icon: 'supplements',
        module: "topics",
        action: "homeTopicClick",
        id: "supplements",
        keyword: 'suplemento',
        tab: 0,
        placements: ['content'],
        homeOrder: 5,
        description: "Informações sobre suplementos utilizados na medicina veterinária."
    },
    {
        name: "Patologia Clínica",
        shortName: "Patologia clínica",
        module: "topics",
        action: "homeTopicClick",
        id: 'clinical_patology',
        icon: 'clinicalPatology',
        keyword: 'patologia',
        tab: 0, pro: true,
        placements: ['content'],
        homeOrder: 4,
        description: "Recursos de patologia clínica para diagnósticos detalhados."
    },
    {
        name: "Rações",
        module: "topics",
        action: "homeTopicClick",
        id: "foods",
        icon: 'foods',
        keyword: 'rações',
        tab: 0,
        placements: ['content'],
        homeOrder: 6,
        description: "Conheça as melhores opções de alimentos disponíveis no mercado."
    },
    {
        name: "Higiene Oral",
        shortName: "Higiene oral",
        icon: 'oralHygiene',
        module: "topics",
        action: "homeTopicClick",
        id: "oral_hygiene",
        keyword: 'higiene oral',
        tab: 0,
        placements: ['content'],
        homeOrder: 8,
        description: "Dicas e produtos para a higiene oral dos seus pacientes animais."
    },
    {
        name: "Favoritos",
        module: "preferences",
        action: "openFavorites",
        id: 'favorites',
        icon: 'favoritesIcon',
        keyword: 'favorites',
        tab: 2,
        placements: ['content', 'tools'],
        toolsOrder: 7,
        subtitle: "Seus itens salvos",
        description: "Acesse rapidamente suas seções e informações favoritas.",
    },
    {
        name: "Histórico",
        module: "preferences",
        action: "openRecentlySeen",
        id: 'recentlySeen',
        icon: 'history',
        keyword: 'recentlySeen',
        tab: 2,
        placements: ['content', 'tools'],
        toolsOrder: 8,
        subtitle: "Consultas recentes",
        description: "Veja as informações recentemente acessadas para fácil referência.",
    },

    /* Disabled */
    {
        name: "Medicamentos Sistêmicos",
        icon: 'drugs',
        disabled: true,
        categoryId: 1,
        module: "preferences",
        action: "openDrugs",
        keyword: 'medicamento'
    },
    {
        name: "Dermatológicos Tópicos",
        icon: 'dermatologic',
        disabled: true,
        categoryId: 4,
        keyword: 'dermatológico'
    },
    {
        name: "Vacinas e Biológicos",
        icon: 'vaccines',
        disabled: true,
        categoryId: 2,
        keyword: 'vacina'
    },
    {
        name: "Agentes Oftalmológicos",
        icon: 'ophtalmic',
        disabled: true,
        categoryId: 5,
        keyword: 'agentes oftalmológico'
    },
];

/** Grupos do painel "Todas as seções", derivados do campo legado `tab`. */
const CONTENT_GROUPS = [
    { id: 'personal', title: 'Pessoal', tab: 2 },
    { id: 'contents', title: 'Conteúdos', tab: 0 },
    { id: 'clinical-support', title: 'Suporte clínico', tab: 1 },
];

const getSection = (id, section) => {
    if (!section && !!id) {
        return sections.find((s) => {
            return (
                !!s.categoryId &&
                s.categoryId.toString() == `${id}`.trim()
            );
        });
    }
    return section;
}
const goToSection = async ({ id, dispatch, section }) => {
    section = getSection(id, section);
    if (section && !!section.categoryId) {
        const path = section.categoryId ? `/items?category=${section.categoryId}` : `/topics/${section.id}`;
        dispatch('general/routePathRedirect', { path }, { root: true })
    } else if (section && !!section.action) {
        dispatch(`${section.module}/${section.action}`, section.id, { root: true })
    }
}

/** Rótulo curto da seção, com fallback para o nome completo. */
const getSectionLabel = (section) => {
    if (!section) return "";
    return section.shortName || section.name;
};

const isEnabled = (section, toggles) => {
    if (!section || section.disabled) return false;
    return !section.toggle || !!(toggles && toggles[section.toggle]);
};

const sortBy = (field) => (a, b) => (a[field] || 0) - (b[field] || 0);

/** Atalhos de "Acesso rápido" do desktop, em ordem. */
const getHomeSections = (toggles) => {
    return sections
        .filter((s) => !!s.homeOrder && isEnabled(s, toggles))
        .sort(sortBy('homeOrder'));
};

/** Atalhos de "Acesso rápido" do mobile: banner de Medicamentos e a linha de círculos. */
const getMobileHomeSections = (toggles) => {
    return sections
        .filter((s) => !!s.homeMobileOrder && isEnabled(s, toggles))
        .sort(sortBy('homeMobileOrder'));
};

/** Itens do painel Ferramentas, em ordem. */
const getToolsSections = (toggles) => {
    return sections
        .filter((s) => (s.placements || []).includes('tools') && isEnabled(s, toggles))
        .sort(sortBy('toolsOrder'));
};

/**
 * Todas as seções de conteúdo e suporte clínico, para o Acesso rápido do
 * desktop: primeiro as que têm homeOrder, depois as demais na ordem do catálogo.
 * Favoritos e Histórico ficam de fora porque já têm lugar próprio na Home.
 */
const getAllSections = (toggles) => {
    const ordered = getHomeSections(toggles);
    const rest = sections.filter(
        (s) =>
            (s.placements || []).includes('content') &&
            (s.tab === 0 || s.tab === 1) &&
            !s.homeOrder &&
            isEnabled(s, toggles)
    );
    return ordered.concat(rest);
};

/** Grupos do painel "Todas as seções" (grupos vazios são omitidos). */
const getContentSectionGroups = (toggles) => {
    return CONTENT_GROUPS.map((group) => ({
        id: group.id,
        title: group.title,
        items: sections.filter(
            (s) =>
                (s.placements || []).includes('content') &&
                s.tab === group.tab &&
                isEnabled(s, toggles)
        ),
    })).filter((group) => group.items.length > 0);
};

export default {
    CONTENT_GROUPS,
    getAllSections,
    getContentSectionGroups,
    getHomeSections,
    getMobileHomeSections,
    getSection,
    getSectionLabel,
    getToolsSections,
    goToSection,
    isEnabled,
    sections
}
